import axios, { AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import {
    AddReviewerPayload,
    AddReviewerResponse,
    ListReviewersResponse,
    ReviewerActivatePayload,
    ReviewerActivateResponse,
    ReviewerOTPRequestPayload,
    ReviewerOTPRequestResponse,
    ReviewerOTPVerifyPayload,
    ReviewerOTPVerifyResponse,
    SharedCandidateProfileResponse,
} from "@type/reviewer";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const addReviewer = async (
    workspaceId: string,
    payload: AddReviewerPayload
): Promise<AddReviewerResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/workspaces/${workspaceId}/reviewers`,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getCookie("authToken")}`,
            },
            data: payload,
        });
        return response.data;
    } catch (error) {
        catchErrorLog(`addReviewer error: ${error}`);
        throw error;
    }
};

export const listReviewers = async (
    workspaceId: string
): Promise<ListReviewersResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/workspaces/${workspaceId}/reviewers`,
            method: "GET",
            headers: {
                Authorization: `Bearer ${getCookie("authToken")}`,
            },
        });
        return response.data;
    } catch (error) {
        catchErrorLog(`listReviewers error: ${error}`);
        throw error;
    }
};

export const removeReviewer = async (
    workspaceId: string,
    reviewerId: string
): Promise<{ code: number; message: string }> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/workspaces/${workspaceId}/reviewers/${reviewerId}`,
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${getCookie("authToken")}`,
            },
        });
        return response.data;
    } catch (error) {
        catchErrorLog(`removeReviewer error: ${error}`);
        throw error;
    }
};

export const resendReviewerInvitation = async (
    workspaceId: string,
    reviewerId: string
): Promise<{ code: number; message: string }> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/workspaces/${workspaceId}/reviewers/${reviewerId}/resend-invitation`,
            method: "POST",
            headers: {
                Authorization: `Bearer ${getCookie("authToken")}`,
            },
        });
        return response.data;
    } catch (error) {
        catchErrorLog(`resendReviewerInvitation error: ${error}`);
        throw error;
    }
};

export const revokeReviewerSessions = async (
    workspaceId: string,
    reviewerId: string
): Promise<{ code: number; message: string }> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/workspaces/${workspaceId}/reviewers/${reviewerId}/revoke`,
            method: "POST",
            headers: {
                Authorization: `Bearer ${getCookie("authToken")}`,
            },
        });
        return response.data;
    } catch (error) {
        catchErrorLog(`revokeReviewerSessions error: ${error}`);
        throw error;
    }
};

export const activateReviewer = async (
    payload: ReviewerActivatePayload
): Promise<ReviewerActivateResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/reviewers/activate`,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            data: payload,
        });
        return response.data;
    } catch (error: any) {
        catchErrorLog(`activateReviewer error: ${error}`);
        if (error?.response?.data) return error.response.data;
        throw error;
    }
};

export const requestReviewerOTP = async (
    payload: ReviewerOTPRequestPayload
): Promise<ReviewerOTPRequestResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/reviewers/otp/request`,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            data: payload,
        });
        return response.data;
    } catch (error: any) {
        catchErrorLog(`requestReviewerOTP error: ${error}`);
        if (error?.response?.data) return error.response.data;
        throw error;
    }
};

export const verifyReviewerOTP = async (
    payload: ReviewerOTPVerifyPayload
): Promise<ReviewerOTPVerifyResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/reviewers/otp/verify`,
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            data: payload,
        });
        return response.data;
    } catch (error: any) {
        catchErrorLog(`verifyReviewerOTP error: ${error}`);
        if (error?.response?.data) return error.response.data;
        throw error;
    }
};

export const getSharedCandidateProfile = async (
    cvId: string,
    jobId?: string
): Promise<SharedCandidateProfileResponse> => {
    try {
        const response: AxiosResponse = await axios({
            url: `${apiURL}/reviewers/candidates/${cvId}`,
            method: "GET",
            params: {
                token: getCookie("REVIEWER_TOKEN"),
                ...(jobId ? { job_id: jobId } : {}),
            },
        });
        return response.data;
    } catch (error: any) {
        catchErrorLog(`getSharedCandidateProfile error: ${error}`);
        if (error?.response?.data) return error.response.data;
        throw error;
    }
};
